import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import { useTimesheetData } from '../../hooks/useTimesheetData';
import timesheetService from '../../services/timesheetService';
import timesheetSyncService, {
    getActiveWeekState, EVT_ACTIVE_WEEK_CHANGED, getOthersHours, EVT_OTHERS_HOURS_CHANGED,
    EVT_CONFLICTS_RESOLVED,
} from '../../services/timesheetSyncService';

import TimesheetHeader from './TimesheetHeader';
import TimesheetTable from './TimesheetTable';
import TimesheetKeyboard from './TimesheetKeyboard';

import SelectEmployeesAndTempsModal from '../jha/SelectEmployeesAndTempsModal';
import SelectFromWeeklyRosterModal from '../jha/SelectFromWeeklyRosterModal';
import SelectJobsModal from './modals/SelectJobsModal';
import CellEditModal from './modals/CellEditModal';
import { zeroingPlan } from './hoursZeroing';
import { divisionOf } from './gridLabels';
import { previousDayPlan } from './copyDay';
import DailyNotesModal from './modals/DailyNotesModal';
import EmployeeNotesModal from './modals/EmployeeNotesModal';
import CalendarPickerModal from './modals/CalendarPickerModal';
import SignTimesheetModal from './modals/SignTimesheetModal';
import WeekViewModal from './modals/WeekViewModal';
import ClearTimesheetModal from './modals/ClearTimesheetModal';
import RejectionCorrectionModal from './modals/RejectionCorrectionModal';
import EmployeeOtherHoursModal from './modals/EmployeeOtherHoursModal';
import TimesheetSyncStatusBar from './TimesheetSyncStatusBar';

function getLocalDateString(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatWeekEndingDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

function getWeekdayName(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
}

/**
 * Чужие часы человека за день, по форманам: [{ name, hours, jobs: ['#7560', …] }].
 * Источник — выжимка othersHours с сервера (своих записей там нет).
 */
function othersToday(othersWeek, employeeId, date) {
    const byName = new Map();
    for (const r of othersWeek || []) {
        if (r.employeeId !== employeeId || r.workDate !== date || !(r.hours > 0)) continue;
        const name = r.createdByName || 'Another foreman';
        const agg = byName.get(name) || { name, hours: 0, jobs: [] };
        agg.hours += r.hours;
        const code = r.jobCode ? `#${r.jobCode}` : '';
        if (code && !agg.jobs.includes(code)) agg.jobs.push(code);
        byName.set(name, agg);
    }
    return [...byName.values()];
}

/** «Tuesday 10/13» — день недели вместе с датой, чтобы не спутать недели. */
function formatDayLabel(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return `${getWeekdayName(dateStr)} ${date.getMonth() + 1}/${date.getDate()}`;
}

export default function TimesheetPage() {
    const navigate = useNavigate();

    // Reference data
    const [employees, setEmployees] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Timesheet data
    const {
        currentDate,
        records,
        recordsDate,
        weekRecords,
        dailyNotes,
        weekDailyNotes,
        selectedEmployeeIds,
        selectedJobIds,
        weekInfo,
        loadDate,
        addRecord,
        updateRecord,
        removeRecord,
        findCellRecord,
        addDailyNote,
        propagateEmployeeNight,
        clearEmployeeNight,
        setAttendCodeForDates,
        signAllTimesheets,
        setSelectedEmployees,
        setSelectedJobs,
        removeEmployeeFromDay,
        deleteWeekHoursOf,
        removeJobFromList,
        clearWeek,
        clearDay,
        deleteRecordsByClientIds,
        fetchCalendarData,
        getPreviousWeekSelections,
        addToSelections,
        copyEntriesToCurrentDay,
    } = useTimesheetData();

    // Modal states
    const [employeesModal, setEmployeesModal] = useState(false);
    const [rosterModal, setRosterModal] = useState(false);
    const [jobsModal, setJobsModal] = useState(false);
    const [dailyNotesModal, setDailyNotesModal] = useState(false);
    const [calendarModal, setCalendarModal] = useState(false);
    const [signModal, setSignModal] = useState(false);
    const [calendarData, setCalendarData] = useState({});
    const [calendarLoading, setCalendarLoading] = useState(false);

    // Cell edit modal state
    const [cellEditModal, setCellEditModal] = useState({ open: false });
    const [rejectionModal, setRejectionModal] = useState({ open: false });

    // Employee notes modal state
    const [employeeNotesModal, setEmployeeNotesModal] = useState({
        open: false, employeeId: 0, employeeName: '',
    });

    // "Other foremen" hours modal — детали чужих часов сотрудника за неделю
    const [otherHoursModal, setOtherHoursModal] = useState({
        open: false, employeeName: '', records: [],
    });

    // Текущий userId из localStorage. Используется в TimesheetTable для фильтра
    // "записи других бригадиров". Один раз на mount — userId не меняется без logout.
    const currentUserId = useMemo(() => {
        const raw = localStorage.getItem('userId');
        return raw ? parseInt(raw, 10) : null;
    }, []);

    // Keyboard state
    const [activeCell, setActiveCell] = useState(null);
    const activeCellRef = useRef(null);
    useEffect(() => { activeCellRef.current = activeCell; }, [activeCell]);
    const [weekViewOpen, setWeekViewOpen] = useState(false);
    const [keyboardValue, setKeyboardValue] = useState('');
    const [keyboardOpen, setKeyboardOpen] = useState(false);

    // Sort state — запоминаем: смена дня, выход с экрана и автообновление
    // приложения сортировку не сбрасывают.
    const savedSort = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('timesheetSort')) || {}; } catch { return {}; }
    }, []);
    const [employeeSort, setEmployeeSort] = useState(savedSort.employeeSort || 'default');
    // Сортировка листа по часам одной работы (долгое нажатие на её шапку).
    const [jobSortId, setJobSortId] = useState(savedSort.jobSortId ?? null);
    // День, на котором отсортировали по работе: на других днях недели лист стоит
    // по часам этого дня, пока не выберут другую сортировку (Скотт, 10/28).
    // Сменилась неделя — сортировка по работе сбрасывается: работы там может не быть.
    const [jobSortDate, setJobSortDate] = useState(savedSort.jobSortDate || null);
    useEffect(() => {
        try {
            localStorage.setItem('timesheetSort', JSON.stringify({ employeeSort, jobSortId, jobSortDate }));
        } catch { /* приватный режим */ }
    }, [employeeSort, jobSortId, jobSortDate]);
    useEffect(() => {
        if (jobSortId == null || !jobSortDate || !currentDate) return;
        if (!timesheetService.getWeekDates(currentDate).includes(jobSortDate)) {
            setJobSortId(null);
            setJobSortDate(null);
        }
    }, [currentDate, jobSortId, jobSortDate]);

    // Clear modal scope: null | 'day' | 'week'
    const [clearScope, setClearScope] = useState(null);

    // Keyboard actual height
    const [keyboardHeight, setKeyboardHeight] = useState(0);

    // Legend — which item is expanded
    const [legendOpen, setLegendOpen] = useState(null);

    // Selected jobs objects
    const selectedJobs = useMemo(() =>
        selectedJobIds.map(id => jobs.find(j => j.id === id)).filter(j => j !== undefined),
        [jobs, selectedJobIds]
    );

    // Индексы для O(1) lookup'а — иначе getRecordForCell был бы O(n) на каждую из 1000 клеток.
    const recordByCell = useMemo(() => {
        const map = new Map();
        for (const r of records) {
            if (r.isDeleted) continue;
            // Старый дубль клетки (до 10/2026) — показываем последнюю правленую,
            // ту же, что найдёт ввод (findCellRecord): видно то, что правишь.
            const key = `${r.employeeId}|${r.jobId}`;
            const cur = map.get(key);
            if (!cur || String(r.updatedAt || '') > String(cur.updatedAt || '')) map.set(key, r);
        }
        return map;
    }, [records]);

    // Часы на работе сортировки в день сортировки: {employeeId: hours} или null.
    const jobSortHours = useMemo(() => {
        if (jobSortId == null) return null;
        const day = jobSortDate || currentDate;
        const hrs = new Map();
        const seen = new Map();
        for (const r of (day === currentDate ? records : weekRecords)) {
            if (r.isDeleted || r.jobId !== jobSortId || r.workDate !== day) continue;
            // Старый дубль клетки — берём последнюю правленую, как в recordByCell.
            const cur = seen.get(r.employeeId);
            if (cur && String(cur.updatedAt || '') >= String(r.updatedAt || '')) continue;
            seen.set(r.employeeId, r);
            hrs.set(r.employeeId, r.hours || 0);
        }
        return hrs;
    }, [jobSortId, jobSortDate, currentDate, records, weekRecords]);

    // Selected employees (sorted) — single source of truth for table & keyboard navigation
    const selectedEmployees = useMemo(() => {
        const list = selectedEmployeeIds
            .map(id => employees.find(e => e.id === id))
            .filter(e => e !== undefined);

        if (jobSortHours) {
            // Больше часов на этой работе в день сортировки — выше; равные стоят
            // как стояли. Перестраивается сразу, на каждое изменение часов того
            // дня (юзер 10/15), — и на остальных днях недели тоже.
            return [...list].sort((a, b) => (jobSortHours.get(b.id) || 0) - (jobSortHours.get(a.id) || 0));
        }
        if (employeeSort === 'asc') {
            return [...list].sort((a, b) =>
                timesheetService.getEmployeeName(a).localeCompare(timesheetService.getEmployeeName(b))
            );
        }
        if (employeeSort === 'desc') {
            return [...list].sort((a, b) =>
                timesheetService.getEmployeeName(b).localeCompare(timesheetService.getEmployeeName(a))
            );
        }
        return list;
    }, [employees, selectedEmployeeIds, employeeSort, jobSortHours]);

    // Лист перестроился, пока вводят часы — ячейка ввода могла уехать за край:
    // докручиваем к ней. Номер строки в activeCell не нужен: навигация
    // клавиатурой ищет строку заново по employeeId.
    const rowOrderKey = selectedEmployees.map(e => e.id).join(',');
    useEffect(() => {
        if (!keyboardOpen || !activeCell) return;
        document.getElementById(`cell-${activeCell.employeeId}-${activeCell.jobId}`)
            ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowOrderKey]);

    const handleSetEmployeeSort = useCallback((next) => {
        setJobSortId(null);
        setEmployeeSort(next);
    }, []);

    const handleToggleJobSort = useCallback((jobId) => {
        setEmployeeSort('default');
        setJobSortId(prev => (prev === jobId ? null : jobId));
        setJobSortDate(currentDate);
    }, [currentDate]);

    // Рабочая неделя приходит с сервера (админ открывает/закрывает недели).
    // Пока сервер молчит — weekEnding null, и всё работает как раньше.
    const [serverWeek, setServerWeek] = useState(() => getActiveWeekState());

    // Часы ДРУГИХ форманов по людям листа за открытую неделю — выжимка с
    // сервера (не записи: править их тут нельзя). Колонка Other и предупреждение
    // при вводе.
    const viewWeekEnding = timesheetService.getWeekEndingDate(currentDate);
    const [othersWeek, setOthersWeek] = useState(() => getOthersHours(viewWeekEnding));
    useEffect(() => {
        setOthersWeek(getOthersHours(viewWeekEnding));
        const onChanged = (e) => {
            if (e.detail?.weekEnding === viewWeekEnding) setOthersWeek(getOthersHours(viewWeekEnding));
        };
        window.addEventListener(EVT_OTHERS_HOURS_CHANGED, onChanged);
        return () => window.removeEventListener(EVT_OTHERS_HOURS_CHANGED, onChanged);
    }, [viewWeekEnding]);
    const othersWeekRef = useRef(othersWeek);
    useEffect(() => { othersWeekRef.current = othersWeek; }, [othersWeek]);

    // Зависшие конфликты перечитаны с сервера — коротко сообщаем, что произошло.
    useEffect(() => {
        const onResolved = (e) => {
            const n = e.detail?.count || 0;
            if (!n) return;
            Swal.fire({
                toast: true, position: 'top', icon: 'info', timer: 4000, showConfirmButton: false,
                title: `${n} ${n === 1 ? 'record was' : 'records were'} refreshed from the server`,
            });
        };
        window.addEventListener(EVT_CONFLICTS_RESOLVED, onResolved);
        return () => window.removeEventListener(EVT_CONFLICTS_RESOLVED, onResolved);
    }, []);

    // Past week — historical, readonly
    // Разрешения правки прошлых недель (админ открыл конкретную job+week).
    // Приходят с /api/timesheet/updates и лежат в localStorage как "jobId|weekEnding".
    // Зависимость от weekRecords — чтобы перечитать после pull/навигации.
    const editUnlocks = useMemo(() => {
        try { return new Set(JSON.parse(localStorage.getItem('timesheetEditUnlocks') || '[]')); }
        catch { return new Set(); }
    }, [currentDate, weekRecords]);

    // У просматриваемой недели есть хоть один разблокированный (job, week).
    const weekHasEditUnlock = useMemo(() => {
        const viewWeekEnd = timesheetService.getWeekEndingDate(currentDate);
        for (const k of editUnlocks) {
            if (k.endsWith('|' + viewWeekEnd)) return true;
        }
        return false;
    }, [editUnlocks, currentDate]);

    const isReadOnlyPastWeek = useMemo(() => {
        const viewWeekEnd = timesheetService.getWeekEndingDate(currentDate);

        // Явное разрешение админа на (job, week) сильнее всего остального.
        if (weekHasEditUnlock) return false;

        // Неделю закрыл админ — история, правкам здесь не место.
        if ((serverWeek.closedWeeks || []).includes(viewWeekEnd)) return true;

        // Сервер управляет неделями: рабочая — активная, всё раньше неё
        // история. Недели ПОЗЖЕ активной не блокируем: админ мог ещё не
        // закрыть прошлую, и люди уже вносят часы за текущую.
        if (serverWeek.weekEnding) return viewWeekEnd < serverWeek.weekEnding;

        // Сервер про недели ещё не сказал — старое поведение по календарю.
        return viewWeekEnd < timesheetService.getWeekEndingDate(getLocalDateString());
    }, [currentDate, weekHasEditUnlock, serverWeek]);

    // Counters
    const approvedCount = useMemo(() => {
        return (weekRecords || []).filter(r => !r.isDeleted && r.status === 'approved').length;
    }, [weekRecords]);
    const totalWeekRecords = useMemo(() => {
        return (weekRecords || []).filter(r => !r.isDeleted).length;
    }, [weekRecords]);
    const isWeekApproved = approvedCount > 0;
    const isWeekFullyApproved = approvedCount > 0 && approvedCount === totalWeekRecords;

    // FULLY approved week = весь UI заблокирован (как прошлая неделя):
    //   - нельзя менять hours ни в одной ячейке
    //   - нельзя добавлять/редактировать cell-notes
    //   - нельзя добавлять daily-job-notes
    //   - нельзя submit / clear
    // Подписать можно и такую неделю, и прошлую: подпись часов не меняет.
    // PARTIALLY approved = только approved-ячейки заблокированы индивидуально
    // (через TimesheetCell.effectiveReadOnly). Остальная работа идёт.
    const isReadOnly = isReadOnlyPastWeek || isWeekFullyApproved;

    // Max navigable date
    const maxNavigableDate = useMemo(() => {
        return timesheetService.getWeekEndingDate(getLocalDateString());
    }, []);

    // Week fully signed
    const isWeekFullySigned = useMemo(() => {
        if (!weekInfo?.signedJobs) return false;
        const jobsWithHours = new Set(
            weekRecords
                .filter(r => !r.isDeleted && r.hours > 0)
                .map(r => r.jobId)
        );
        if (jobsWithHours.size === 0) return false;
        for (const jobId of jobsWithHours) {
            const info = weekInfo.signedJobs[jobId.toString()];
            if (!info || info.status !== 'signed') return false;
        }
        return true;
    }, [weekInfo, weekRecords]);

    // Refs для часто меняющегося состояния — нужны, чтобы handleCellShortPress/
    // handleCellLongPress были стабильными (React.memo на TimesheetCell
    // работает ТОЛЬКО если коллбэки не пересоздаются при каждом save).
    const recordsRef = useRef(records);
    const weekRecordsRef = useRef(weekRecords);
    const employeesRef = useRef(employees);
    const jobsRef = useRef(jobs);
    const currentDateRef = useRef(currentDate);
    const isReadOnlyRef = useRef(isReadOnly);

    useEffect(() => { recordsRef.current = records; }, [records]);
    useEffect(() => { weekRecordsRef.current = weekRecords; }, [weekRecords]);
    useEffect(() => { employeesRef.current = employees; }, [employees]);
    useEffect(() => { jobsRef.current = jobs; }, [jobs]);
    useEffect(() => { currentDateRef.current = currentDate; }, [currentDate]);
    useEffect(() => { isReadOnlyRef.current = isReadOnly; }, [isReadOnly]);

    // ─── Load reference data ────────────────────────────────────────────────

    useEffect(() => {
        let retry = null, attempts = 0;
        const load = async () => {
            clearTimeout(retry);
            try {
                const [emps, temps, jbs] = await Promise.all([
                    timesheetService.getAllEmployees(),
                    timesheetService.getAllTemps(),
                    timesheetService.getAllJobs(),
                ]);
                setEmployees([...emps, ...temps]);
                setJobs(jbs);
                attempts = 0;
            } catch (e) {
                // База как раз переоткрывается синком — читаем ещё раз чуть позже.
                console.warn('[Timesheet] reference data read failed, retrying:', e);
                if (attempts++ < 5) retry = setTimeout(load, 1500);
            } finally {
                setLoading(false);
            }
        };
        load();
        // Синк справочников (кнопка Sync, вход, после обновления приложения)
        // очищает и заново заливает сотрудников и работы. Страницу, открытую в
        // это время, читала пустые справочники — и таблица выглядела пустой,
        // хотя часы на месте (10/21, после автообновления). Перечитываем, когда
        // синк закончился.
        window.addEventListener('syncComplete', load);
        // Создан код работы (7888-1) — он уже в справочнике, показываем в списке работ.
        window.addEventListener('timesheetJobsChanged', load);
        return () => {
            clearTimeout(retry);
            window.removeEventListener('syncComplete', load);
            window.removeEventListener('timesheetJobsChanged', load);
        };
    }, []);

    // Код работы, заведённый из заметок сотрудника (7888-1), — сразу в таймшит
    // недели строкой со своими клеточками.
    useEffect(() => {
        const onJobs = (e) => {
            const ids = (e.detail && e.detail.addToGrid) || [];
            if (ids.length) addToSelections([], ids);
        };
        window.addEventListener('timesheetJobsChanged', onJobs);
        return () => window.removeEventListener('timesheetJobsChanged', onJobs);
    }, [addToSelections]);

    // Фоновая синхронизация с сервером. Когда бэкенд ещё не готов,
    // сервис делает запросы, получает ошибки и уходит в backoff — но не ломает UI.
    useEffect(() => {
        timesheetSyncService.init();
        return () => {
            timesheetSyncService.destroy();
        };
    }, []);

    // Подпись — необязательная опция. Никаких проверок и напоминаний про
    // неподписанную прошлую неделю здесь нет и быть не должно.

    // Админ закрыл неделю и открыл следующую — узнаём об этом на ближайшем
    // автосинке. Только сообщаем: не уводим с текущего экрана и ничего не
    // стираем локально, старые недели остаются на месте (read-only).
    useEffect(() => {
        const onWeekChanged = (e) => {
            setServerWeek(getActiveWeekState());
            const { previous, current, weekStart } = e.detail || {};
            // Первый ответ сервера (previous пуст) — не новость, просто узнали.
            if (!previous || !current || previous === current) return;

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'info',
                title: 'New week started',
                text: `${formatWeekEndingDate(weekStart || current)} — ${formatWeekEndingDate(current)}`,
                showConfirmButton: true,
                confirmButtonText: 'Open',
                timer: 12000,
                timerProgressBar: true,
            }).then((r) => {
                if (r.isConfirmed && weekStart) loadDate(weekStart);
            });
        };
        window.addEventListener(EVT_ACTIVE_WEEK_CHANGED, onWeekChanged);
        return () => window.removeEventListener(EVT_ACTIVE_WEEK_CHANGED, onWeekChanged);
    }, [loadDate]);

    // Значение клавиатуры — из записи клетки, но только когда сменились клетка
    // или день (и записи этого дня уже загружены). На каждое сохранение НЕ
    // перетираем: сохранения идут по очереди и отстают от нажатий, и ответ на
    // «1» возвращал уже набранное «10» обратно в «1» — цифры мигали и пропадали
    // (айпад, 10/15).
    // Сразу при смене клетки или дня — из того, что уже в памяти (неделя целиком),
    // чтобы в клавиатуре не висело значение прошлого дня. Догрузились записи дня
    // (другая неделя) — ещё раз, если с тех пор ничего не набрали.
    const kbSyncRef = useRef({ key: '', loaded: false, typed: false });
    useEffect(() => {
        if (!activeCell || !keyboardOpen) {
            kbSyncRef.current = { key: '', loaded: false, typed: false };
            return;
        }
        const key = `${currentDate}|${activeCell.employeeId}|${activeCell.jobId}`;
        const loaded = recordsDate === currentDate;
        const st = kbSyncRef.current;
        if (st.key === key && (st.loaded || !loaded || st.typed)) {
            if (loaded) st.loaded = true;
            return;
        }
        kbSyncRef.current = { key, loaded, typed: false };
        const existingHours = findCellRecord(activeCell.employeeId, activeCell.jobId, currentDate)?.hours || 0;
        setKeyboardValue(existingHours > 0 ? existingHours.toString() : '');
    }, [currentDate, recordsDate, activeCell, keyboardOpen, findCellRecord]);

    // ─── Future day guard ───────────────────────────────────────────────────
    //
    // Данные на день, который ещё не наступил, почти всегда ошибка недели: 10/12
    // форман принял текущий понедельник за прошлый и вбил неделю Пн–Пт вперёд.
    // Не запрещаем, а спрашиваем — один раз на дату за сессию и ДО того, как
    // ячейка откроется: автосохранение пишет каждое нажатие, и вопрос посреди
    // ввода оставил бы на сервере уже уехавшие цифры.
    const confirmedFutureDatesRef = useRef(new Set());

    const needsFutureConfirm = useCallback((date) =>
        date > getLocalDateString() && !confirmedFutureDatesRef.current.has(date), []);

    const confirmFutureDate = useCallback(async (date) => {
        if (!needsFutureConfirm(date)) return true;
        const result = await Swal.fire({
            icon: 'warning',
            title: 'This day hasn\'t happened yet',
            html: `Today is <b>${formatDayLabel(getLocalDateString())}</b>.<br>`
                + `You are entering data for <b>${formatDayLabel(date)}</b>.<br><br>`
                + '<small>If these hours are for last week, tap the week box at the top left and pick the right day.</small>',
            showCancelButton: true,
            confirmButtonText: `Yes, ${formatDayLabel(date)}`,
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#1976d2',
        });
        if (!result.isConfirmed) return false;
        confirmedFutureDatesRef.current.add(date);
        return true;
    }, [needsFutureConfirm]);

    // Клавиатура открыта, а день сменили — ячейка едет на новую дату. На будущий
    // день без подтверждения не везём: закрываем клавиатуру, вопрос задаст
    // следующий тап по ячейке.
    const carryCellTo = useCallback((date) => {
        if (needsFutureConfirm(date)) {
            setKeyboardOpen(false);
            setActiveCell(null);
            setKeyboardValue('');
            return;
        }
        setActiveCell(prev => prev ? { ...prev, workDate: date } : null);
    }, [needsFutureConfirm]);

    // ─── Navigation ─────────────────────────────────────────────────────────

    // Гейта «сперва подпиши неделю» больше нет: подпись — необязательная
    // опция, и неподписанная неделя не должна мешать ходить по датам.

    // Свежая saveKeyboardCell для перехода по дням. Она объявлена ниже и в
    // зависимости navigateDate не попадала: при уходе со дня сохранялась СТАРАЯ
    // версия со старым значением клавиатуры — 10/15 на айпаде «7 в среду, 6 в
    // четверг, ушёл с четверга — в четверге снова 7» (или часы удалялись).
    const saveKeyboardCellRef = useRef(null);

    const navigateDate = useCallback(async (direction) => {
        const date = new Date(currentDate + 'T00:00:00');
        const dayOfWeek = date.getDay();

        if (direction === 1 && dayOfWeek === 0) {
            date.setDate(date.getDate() - 6);
        } else if (direction === -1 && dayOfWeek === 1) {
            const sundayDate = new Date(date);
            sundayDate.setDate(date.getDate() + 6);
            const sundayStr = getLocalDateString(sundayDate);
            if (sundayStr > maxNavigableDate) return;
            date.setDate(date.getDate() + 6);
        } else {
            date.setDate(date.getDate() + direction);
        }

        const newDateStr = getLocalDateString(date);
        if (newDateStr > maxNavigableDate) return;

        // Auto-save if keyboard open
        if (keyboardOpen && activeCell && activeCell.workDate === currentDate) {
            await saveKeyboardCellRef.current?.();
        }

        carryCellTo(newDateStr);
        loadDate(newDateStr);
    }, [currentDate, maxNavigableDate, keyboardOpen, activeCell, loadDate, carryCellTo]);

    const goToToday = useCallback(() => {
        loadDate(getLocalDateString());
    }, [loadDate]);

    // ─── Helpers ────────────────────────────────────────────────────────────

    // recordByCell — выше, рядом с сортировкой листа.
    const dayTotalByEmployee = useMemo(() => {
        const map = new Map();
        for (const r of records) {
            if (r.isDeleted) continue;
            map.set(r.employeeId, (map.get(r.employeeId) || 0) + (r.hours || 0));
        }
        return map;
    }, [records]);

    const weekStatsByEmployee = useMemo(() => {
        const totals = new Map();
        const datesByEmp = new Map();
        for (const r of weekRecords) {
            if (r.isDeleted) continue;
            totals.set(r.employeeId, (totals.get(r.employeeId) || 0) + (r.hours || 0));
            if (r.hours > 0) {
                if (!datesByEmp.has(r.employeeId)) datesByEmp.set(r.employeeId, new Set());
                datesByEmp.get(r.employeeId).add(r.workDate);
            }
        }
        return { totals, datesByEmp };
    }, [weekRecords]);

    const getRecordForCell = useCallback((employeeId, jobId) => {
        return recordByCell.get(`${employeeId}|${jobId}`);
    }, [recordByCell]);

    const getHoursForCell = useCallback((employeeId, jobId) => {
        return recordByCell.get(`${employeeId}|${jobId}`)?.hours || 0;
    }, [recordByCell]);

    const getDayTotal = useCallback((employeeId) => {
        return dayTotalByEmployee.get(employeeId) || 0;
    }, [dayTotalByEmployee]);

    const getWeekTotal = useCallback((employeeId) => {
        return weekStatsByEmployee.totals.get(employeeId) || 0;
    }, [weekStatsByEmployee]);

    const getDaysWorked = useCallback((employeeId) => {
        return weekStatsByEmployee.datesByEmp.get(employeeId)?.size || 0;
    }, [weekStatsByEmployee]);

    // ─── Keyboard handlers ──────────────────────────────────────────────────

    // Этому человеку в этот день уже ставил часы другой форман — предупреждаем, но
    // ввод не держим: часы уйдут, решать форману. Один раз на человека и день,
    // иначе тост вылезал бы на каждой его клетке.
    const othersNoticedRef = useRef(new Set());
    const noticeOtherForemen = useCallback((employeeId) => {
        const date = currentDateRef.current;
        const key = `${employeeId}|${date}`;
        if (othersNoticedRef.current.has(key)) return;
        const others = othersToday(othersWeekRef.current, employeeId, date);
        if (!others.length) return;
        othersNoticedRef.current.add(key);

        const esc = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        const name = timesheetService.getEmployeeName(employeesRef.current.find(e => e.id === employeeId));
        const othersTotal = others.reduce((s, o) => s + o.hours, 0);
        const mine = recordsRef.current
            .filter(r => r.employeeId === employeeId && !r.isDeleted)
            .reduce((s, r) => s + (r.hours || 0), 0);
        const round = (n) => Math.round(n * 100) / 100;
        Swal.fire({
            toast: true,
            position: 'top',
            icon: 'warning',
            title: `${name}: ${round(othersTotal)} h already today from another foreman`,
            html: others.map(o => `<div><b>${esc(o.name)}</b> — ${round(o.hours)} h${o.jobs.length ? ' on ' + esc(o.jobs.join(', ')) : ''}</div>`).join('')
                + (mine + othersTotal >= 24
                    ? '<div style="margin-top:4px;color:#b91c1c;">The day is already at 24 h with your hours — the office will not accept more.</div>'
                    : '<div style="margin-top:4px;color:#64748b;">Your hours will still be sent.</div>'),
            showConfirmButton: false,
            showCloseButton: true,
            timer: 8000,
            timerProgressBar: true,
        });
    }, []);

    // Стабильные коллбэки (зависимости = []), читают актуальное состояние через refs.
    // Это критично для React.memo на TimesheetCell — иначе перерендер всех 1000 клеток.
    const handleCellShortPress = useCallback(async (employeeId, jobId, employeeIndex, jobIndex) => {
        // Подпись прошлой недели — ПО ЖЕЛАНИЮ: неподписанная прошлая неделя больше
        // НЕ блокирует ввод (данные и так улетают сразу; пуш идёт из админки).
        if (isReadOnlyRef.current) return;
        if (!(await confirmFutureDate(currentDateRef.current))) return;
        const existing = findCellRecord(employeeId, jobId);

        // Rejected-запись: вместо клавиатуры показываем модалку коррекции
        // с причиной отклонения, прежними часами и подтверждением.
        if (existing && existing.status === 'rejected') {
            const employee = employeesRef.current.find(e => e.id === employeeId);
            const job = jobsRef.current.find(j => j.id === jobId);
            setRejectionModal({
                open: true,
                employeeId,
                jobId,
                employeeName: timesheetService.getEmployeeName(employee),
                jobName: timesheetService.getJobName(job),
                jobCode: job?.job || '',
                workDate: currentDateRef.current,
                originalHours: existing.hours || 0,
                rejectionReason: existing.rejectionReason,
                rejectedByName: existing.rejectedByName,
                rejectedAt: existing.rejectedAt,
            });
            return;
        }

        const existingHours = existing?.hours || 0;
        setActiveCell({ employeeId, jobId, employeeIndex, jobIndex, workDate: currentDateRef.current });
        setKeyboardValue(existingHours > 0 ? existingHours.toString() : '');
        setKeyboardOpen(true);
        noticeOtherForemen(employeeId);
    }, [confirmFutureDate, noticeOtherForemen, findCellRecord]);

    const handleCellLongPress = useCallback(async (employeeId, jobId) => {
        if (isReadOnlyRef.current) return;
        if (!(await confirmFutureDate(currentDateRef.current))) return;
        const employee = employeesRef.current.find(e => e.id === employeeId);
        const job = jobsRef.current.find(j => j.id === jobId);
        const record = findCellRecord(employeeId, jobId);

        const dayTotal = recordsRef.current
            .filter(r => r.employeeId === employeeId && !r.isDeleted)
            .reduce((sum, r) => sum + r.hours, 0);
        const weekTotal = weekRecordsRef.current
            .filter(r => r.employeeId === employeeId && !r.isDeleted)
            .reduce((sum, r) => sum + r.hours, 0);
        const daysWorked = new Set(
            weekRecordsRef.current
                .filter(r => r.employeeId === employeeId && r.hours > 0 && !r.isDeleted)
                .map(r => r.workDate)
        ).size;

        // Часы, уже вписанные ЭТОМУ сотруднику СЕГОДНЯ другими форманами — из
        // выжимки чужих часов (приходит с синком недели; без GET к API). Показываем
        // как предупреждение, чтобы форман не переработал лимит 24ч вслепую.
        const myUserId = timesheetService.getCurrentUserMeta().userId;
        const otherHours = othersToday(othersWeekRef.current, employeeId, currentDateRef.current);

        // Карта {дата: код} по МОИМ записям этого сотрудника на этой работе за неделю —
        // пикер дней показывает реальное состояние недели (что уже проставлено).
        const weekAttend = {};
        weekRecordsRef.current
            .filter(r => r.employeeId === employeeId && r.jobId === jobId && !r.isDeleted && r.attendCode
                && (myUserId == null || Number(r.createdByUserId) === Number(myUserId)))
            .forEach(r => { weekAttend[r.workDate] = r.attendCode; });

        setCellEditModal({
            open: true,
            employeeId,
            employeeName: timesheetService.getEmployeeName(employee),
            jobId,
            jobName: timesheetService.getJobName(job),
            hours: record?.hours || 0,
            notes: record?.notes,
            attendCode: record?.attendCode || '',
            weekSummary: { dayTotal, weekTotal, daysWorked, otherHours },
            weekDates: timesheetService.getWeekDates(currentDateRef.current),
            weekAttend,
        });
    }, [confirmFutureDate, findCellRecord]);

    /** Часы стёрли в ноль — правило и его обоснование в hoursZeroing.js. */
    const zeroOrRemove = useCallback(async (record) => {
        if (zeroingPlan(record) === 'update') {
            await updateRecord(record.clientId, 0, record.attendCode || '', record.jobCode || '', record.notes || '', { skipLimitCheck: true });
            return;
        }
        await removeRecord(record.clientId);
    }, [updateRecord, removeRecord]);

    const handleKeyboardValueChange = useCallback(async (value) => {
        if (value === '' || value === '.' || /^\d*\.?\d*$/.test(value)) {
            const numValue = parseFloat(value);
            if (value === '' || value === '.' || (numValue >= 0 && numValue <= 24)) {
                if (activeCell) {
                    const { employeeId, jobId } = activeCell;
                    const hours = parseFloat(value) || 0;
                    const existingRecord = findCellRecord(employeeId, jobId);

                    // Check 24h limit
                    const currentDayTotal = getDayTotal(employeeId);
                    const existingHours = existingRecord?.hours || 0;
                    const newDayTotal = currentDayTotal - existingHours + hours;

                    if (newDayTotal > 24) {
                        const employee = employees.find(e => e.id === employeeId);
                        Swal.fire({
                            icon: 'error',
                            title: 'Exceeds 24h',
                            text: `${timesheetService.getEmployeeName(employee)}: ${newDayTotal}h total`,
                            timer: 2000,
                            showConfirmButton: false,
                        });
                        return;
                    }

                    setKeyboardValue(value);
                    kbSyncRef.current.typed = true;

                    // Save to IndexedDB immediately. skipLimitCheck — UI уже проверил лимит выше.
                    if (hours > 0) {
                        if (existingRecord) {
                            await updateRecord(existingRecord.clientId, hours, existingRecord.attendCode || '', existingRecord.jobCode || '', existingRecord.notes || '', { skipLimitCheck: true });
                        } else {
                            await addRecord(employeeId, jobId, hours, '', '', '', { skipLimitCheck: true });
                        }
                    } else if (existingRecord) {
                        await zeroOrRemove(existingRecord);
                    }
                } else {
                    setKeyboardValue(value);
                }
            }
        }
    }, [activeCell, records, employees, updateRecord, addRecord, zeroOrRemove, findCellRecord, currentDate]);

    const saveKeyboardCell = useCallback(async () => {
        if (!activeCell) return true;
        const { employeeId, jobId } = activeCell;
        const hours = parseFloat(keyboardValue) || 0;
        const existingRecord = findCellRecord(employeeId, jobId);

        const currentDayTotal = getDayTotal(employeeId);
        const existingHours = existingRecord?.hours || 0;
        const newDayTotal = currentDayTotal - existingHours + hours;

        if (newDayTotal > 24) {
            const employee = employees.find(e => e.id === employeeId);
            Swal.fire({
                icon: 'error',
                title: 'Exceeds 24h',
                text: `${timesheetService.getEmployeeName(employee)}: ${newDayTotal}h total`,
                timer: 2000,
                showConfirmButton: false,
            });
            return false;
        }

        if (hours > 0) {
            if (existingRecord) {
                await updateRecord(existingRecord.clientId, hours, existingRecord.attendCode || '', existingRecord.jobCode || '', existingRecord.notes || '', { skipLimitCheck: true });
            } else {
                await addRecord(employeeId, jobId, hours, '', '', '', { skipLimitCheck: true });
            }
        } else if (existingRecord) {
            await zeroOrRemove(existingRecord);
        }
        return true;
    }, [activeCell, keyboardValue, employees, records, weekRecords, updateRecord, addRecord, zeroOrRemove, findCellRecord, currentDate]);

    saveKeyboardCellRef.current = saveKeyboardCell;

    /**
     * Закрыть клавиатуру БЕЗ досохранения — когда часы этой клетки только что
     * удалили (очистка дня/недели, удаление строки/работы, снятие человека).
     * Каждое нажатие и так сохраняется сразу; обычное закрытие досохраняло
     * висящее в клавиатуре значение и возвращало удалённое («поставил 8, Clear
     * day, закрыл клавиатуру — 8 снова на месте», 10/15).
     */
    const dismissKeyboard = useCallback(() => {
        setKeyboardOpen(false);
        setActiveCell(null);
        setKeyboardValue('');
    }, []);

    const closeKeyboard = useCallback(async (deleteExisting) => {
        // Only delete if explicitly passed boolean true (not event object)
        if (deleteExisting === true && activeCell) {
            const { employeeId, jobId } = activeCell;
            const existingRecord = findCellRecord(employeeId, jobId);
            if (existingRecord) {
                await removeRecord(existingRecord.clientId);
            }
        } else {
            // Save current value before closing
            await saveKeyboardCell();
        }
        setKeyboardOpen(false);
        setActiveCell(null);
        setKeyboardValue('');
    }, [activeCell, records, removeRecord, saveKeyboardCell, findCellRecord]);

    const navigateCell = useCallback(async (direction) => {
        if (!activeCell) return;
        await saveKeyboardCell();

        // Re-resolve current employee index against the sorted list — the user may have
        // changed the sort order since the cell was opened, so the stored index is stale.
        const currentIdx = selectedEmployees.findIndex(e => e.id === activeCell.employeeId);
        const employeeIndex = currentIdx >= 0 ? currentIdx : activeCell.employeeIndex;
        const { jobIndex } = activeCell;
        let newEmpIndex = employeeIndex;
        let newJobIndex = jobIndex;

        switch (direction) {
            case 'up': newEmpIndex = Math.max(0, employeeIndex - 1); break;
            case 'down': newEmpIndex = Math.min(selectedEmployees.length - 1, employeeIndex + 1); break;
            case 'left': newJobIndex = Math.max(0, jobIndex - 1); break;
            case 'right': newJobIndex = Math.min(selectedJobs.length - 1, jobIndex + 1); break;
        }

        const newEmployee = selectedEmployees[newEmpIndex];
        const newJob = selectedJobs[newJobIndex];

        if (newEmployee && newJob) {
            setActiveCell({
                employeeId: newEmployee.id,
                jobId: newJob.id,
                employeeIndex: newEmpIndex,
                jobIndex: newJobIndex,
                workDate: currentDate,
            });
            const existingHours = getHoursForCell(newEmployee.id, newJob.id);
            setKeyboardValue(existingHours > 0 ? existingHours.toString() : '');

            const cellEl = document.getElementById(`cell-${newEmployee.id}-${newJob.id}`);
            if (cellEl) cellEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
    }, [activeCell, selectedEmployees, selectedJobs, currentDate, records, saveKeyboardCell]);

    // ─── Cell Edit Modal ────────────────────────────────────────────────────

    const handleSaveCellEdit = useCallback(async (hours, notes, attendCode = '', dayOps = null) => {
        if (!cellEditModal.employeeId || !cellEditModal.jobId) return;
        const { employeeId, jobId } = cellEditModal;
        const existingRecord = findCellRecord(employeeId, jobId);

        if (hours > 24) {
            Swal.fire({ icon: 'error', title: 'Invalid Hours', text: 'Cannot enter more than 24 hours', confirmButtonColor: '#1976d2' });
            return;
        }

        const currentDayTotal = getDayTotal(employeeId);
        const existingHoursForThisCell = existingRecord?.hours || 0;
        const newDayTotal = currentDayTotal - existingHoursForThisCell + hours;

        if (newDayTotal > 24) {
            const employee = employees.find(e => e.id === employeeId);
            Swal.fire({
                icon: 'error',
                title: 'Exceeds 24 Hours',
                text: `${timesheetService.getEmployeeName(employee)} already has ${currentDayTotal - existingHoursForThisCell}h today. Adding ${hours}h would total ${newDayTotal}h.`,
                confirmButtonColor: '#1976d2',
            });
            return;
        }

        // Attendance code — валидная запись даже с 0 часов (absence day):
        // такие строки уходят в payroll как AbsentCode без часов.
        const hadNight = existingRecord?.attendCode === 'Night work';
        if (hours > 0 || notes || attendCode) {
            if (existingRecord) {
                await updateRecord(existingRecord.clientId, hours, attendCode, existingRecord.jobCode || '', notes, { skipLimitCheck: true });
            } else {
                await addRecord(employeeId, jobId, hours, attendCode, '', notes, { skipLimitCheck: true });
            }
            if (!dayOps && attendCode === 'Night work' && !hadNight) {
                // Ночь персональная: впервые поставили NW без явного выбора дней —
                // тянем на остаток недели ЭТОГО сотрудника.
                await propagateEmployeeNight(employeeId, jobId, currentDate);
            }
        } else if (existingRecord) {
            await removeRecord(existingRecord.clientId);
        }
        // Явный мультивыбор дней перебивает авто-протяжку: ставим код на отмеченные
        // дни и снимаем с тех, где галочку убрали (текущий день сохранён выше).
        // dayOps.code — код ОСТАЛЬНЫХ дней; он не обязан совпадать с текущим днём
        // (сняли ночь со среды — Пн и Вт остаются ночными). Обрабатываем и когда
        // текущий день обнулили: снятые галочки должны сработать в любом случае.
        if (dayOps) {
            const dayCode = dayOps.code !== undefined ? dayOps.code : attendCode;
            if (dayOps.apply?.length) await setAttendCodeForDates(employeeId, jobId, dayCode, dayOps.apply);
            if (dayOps.remove?.length) await setAttendCodeForDates(employeeId, jobId, '', dayOps.remove);
        }
        // Ночь на исходном дне сняли/сменили на другой статус → откатываем авто-протяжку
        // на остаток недели (пустые ночные дни удаляем, дни с часами — снимаем код).
        // При явном выборе дней (dayOps) авто-откат не нужен: форман сам указал дни.
        if (!dayOps && hadNight && attendCode !== 'Night work') {
            await clearEmployeeNight(employeeId, jobId, currentDate);
        }

        setCellEditModal({ open: false });
    }, [cellEditModal, employees, records, updateRecord, addRecord, removeRecord, propagateEmployeeNight, clearEmployeeNight, setAttendCodeForDates, currentDate, findCellRecord]);

    // ─── Employee Notes ─────────────────────────────────────────────────────

    const handleOpenEmployeeNotes = useCallback(async (employeeId) => {
        // Здесь ставятся коды отсутствия и заметки — те же данные дня, что и часы.
        if (!(await confirmFutureDate(currentDateRef.current))) return;
        const employee = employees.find(e => e.id === employeeId);
        setEmployeeNotesModal({
            open: true,
            employeeId,
            employeeName: timesheetService.getEmployeeName(employee),
        });
    }, [employees, confirmFutureDate]);

    const handleOpenOtherHours = useCallback((employeeId, employeeName, breakdown) => {
        setOtherHoursModal({
            open: true,
            employeeName: employeeName || '',
            records: breakdown || [],
        });
    }, []);

    const handleSaveEmployeeJobNotes = useCallback(async (jobId, notes) => {
        const employeeId = employeeNotesModal.employeeId;
        if (!employeeId) return;
        const existingRecord = findCellRecord(employeeId, jobId);
        if (existingRecord) {
            await updateRecord(existingRecord.clientId, existingRecord.hours, existingRecord.attendCode || '', existingRecord.jobCode || '', notes);
        } else if (notes && notes.trim()) {
            // Заметка по работе без часов — создаём 0ч-запись (симметрично cell editor).
            await addRecord(employeeId, jobId, 0, '', '', notes, { skipLimitCheck: true });
        }
    }, [employeeNotesModal, records, updateRecord, addRecord, findCellRecord]);

    /**
     * Attendance-код из Employee Notes. Absence-код на работе без часов создаёт
     * 0ч-запись (валидный день отсутствия), а Night work — нет: ночь помечает
     * ОТРАБОТАННЫЕ часы, для пустого дня она бессмысленна (и раньше засоряла payroll).
     * dayOps — опциональный мультивыбор дней недели: { apply: [], remove: [] }.
     */
    const handleSetEmployeeAttendCode = useCallback(async (jobId, code, dayOps = null) => {
        const employeeId = employeeNotesModal.employeeId;
        if (!employeeId) return;
        const existingRecord = findCellRecord(employeeId, jobId);
        const hadNight = existingRecord?.attendCode === 'Night work';
        if (existingRecord) {
            const hasData = Number(existingRecord.hours) > 0 || (existingRecord.notes && String(existingRecord.notes).trim() !== '');
            if (!code && !hasData) {
                // Сняли код, а часов/заметки нет → пустую запись удаляем (не держим 0ч-пустышку).
                await removeRecord(existingRecord.clientId);
            } else {
                await updateRecord(existingRecord.clientId, existingRecord.hours, code || '', existingRecord.jobCode || '', existingRecord.notes || '', { skipLimitCheck: true });
            }
        } else if (code === 'Night work') {
            // Ночь без часов не создаём, но выбор ДРУГИХ дней всё равно применяем ниже.
            Swal.fire({
                icon: 'info',
                title: 'Enter hours first',
                text: 'Night work marks hours that were worked at night. Add the hours for this job, and the night code will be applied automatically.',
                confirmButtonColor: '#1976d2',
            });
        } else if (code) {
            await addRecord(employeeId, jobId, 0, code, '', '', { skipLimitCheck: true });
        }
        // Явный мультивыбор дней (текущий день обработан выше). dayOps.code — код
        // остальных дней: он не обязан совпадать с текущим днём.
        if (dayOps) {
            const dayCode = dayOps.code !== undefined ? dayOps.code : code;
            if (dayOps.apply?.length) await setAttendCodeForDates(employeeId, jobId, dayCode, dayOps.apply);
            if (dayOps.remove?.length) await setAttendCodeForDates(employeeId, jobId, '', dayOps.remove);
        } else if (existingRecord && code === 'Night work' && !hadNight) {
            // Только когда запись на день реально есть — иначе мы бы протянули ночь
            // сразу после подсказки «сначала введите часы».
            await propagateEmployeeNight(employeeId, jobId, currentDate);
        }
        // Ночь сняли/сменили → откат авто-протяжки (при явном выборе дней не нужен).
        if (!dayOps && hadNight && code !== 'Night work') {
            await clearEmployeeNight(employeeId, jobId, currentDate);
        }
    }, [employeeNotesModal, records, updateRecord, addRecord, removeRecord, propagateEmployeeNight, clearEmployeeNight, setAttendCodeForDates, currentDate, findCellRecord]);

    // ─── Remove Employee/Job with confirmation ──────────────────────────────

    /**
     * Сняли людей в окне «+ Employee»: у кого есть часы за неделю — сначала
     * предупреждение, кто и за какие дни их потеряет (как у удаления строки).
     * Подтвердили — часы удаляются; «Keep them» — эти люди остаются в листе.
     * Часы, одобренные для payroll, не удаляются никогда — такие люди остаются.
     * @returns {Promise<number[]>} кого оставить в листе
     */
    const confirmDroppedEmployees = useCallback(async (dropIds) => {
        const recsOf = (id) => weekRecords.filter(r => r.employeeId === id && !r.isDeleted && (r.hours > 0 || r.attendCode));
        const withHours = dropIds.filter(id => recsOf(id).length > 0);
        if (!withHours.length) return [];

        const esc = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        const nameOf = (id) => timesheetService.getEmployeeName(employees.find(e => e.id === id)) || `#${id}`;
        const locked = withHours.filter(id => recsOf(id).some(r => r.status === 'approved'));
        const removable = withHours.filter(id => !locked.includes(id));

        const personHtml = (id) => {
            const byDay = {};
            for (const r of recsOf(id)) {
                const key = `${getWeekdayName(r.workDate)} ${formatWeekEndingDate(r.workDate)}`;
                byDay[key] = byDay[key] || { hours: 0, jobs: [] };
                byDay[key].hours += r.hours || 0;
                const job = jobs.find(j => j.id === r.jobId);
                byDay[key].jobs.push(`#${timesheetService.getJobCode(job) || r.jobId} ${r.hours || 0}h${r.attendCode ? ' · ' + r.attendCode : ''}`);
            }
            const total = Object.values(byDay).reduce((s, d) => s + d.hours, 0);
            return `<div style="margin:6px 0;padding:6px 10px;border:1px solid #eee;border-radius:6px;background:#fafafa;">
                <div style="display:flex;justify-content:space-between;gap:8px;"><strong>${esc(nameOf(id))}</strong>
                    <span style="color:#c62828;font-weight:700;">${Math.round(total * 100) / 100}h</span></div>
                ${Object.entries(byDay).map(([day, d]) => `<div style="display:flex;justify-content:space-between;gap:8px;font-size:12px;color:#555;">
                    <span>${esc(day)}</span><span>${esc(d.jobs.join(', '))}</span></div>`).join('')}
            </div>`;
        };

        if (!removable.length) {
            await Swal.fire({
                icon: 'error',
                title: 'Locked — approved for payroll',
                html: `<div style="text-align:left;font-size:13px;">These people stay in the list — their hours are already approved for payroll:${locked.map(personHtml).join('')}Ask the office to <b>unapprove</b> them first in the admin panel.</div>`,
                confirmButtonText: 'Got it',
            });
            return locked;
        }

        const res = await Swal.fire({
            icon: 'warning',
            title: 'Remove from the list?',
            html: `<div style="text-align:left;font-size:13px;">
                Your hours for these people will be <b>deleted</b> for the week:
                ${removable.map(personHtml).join('')}
                ${locked.length ? `<div style="margin-top:8px;color:#7c3aed;">Staying in the list — approved for payroll: ${locked.map(id => esc(nameOf(id))).join(', ')}</div>` : ''}
            </div>`,
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#757575',
            confirmButtonText: 'Delete hours & remove',
            cancelButtonText: 'Keep them',
            width: 520,
        });
        if (!res.isConfirmed) return withHours;
        if (activeCell && removable.includes(activeCell.employeeId)) dismissKeyboard();
        await deleteWeekHoursOf(removable);
        return locked;
    }, [weekRecords, employees, jobs, deleteWeekHoursOf, activeCell, dismissKeyboard]);

    const handleRemoveEmployee = useCallback(async (employeeId, employeeName) => {
        const employeeWeekRecords = weekRecords.filter(
            r => r.employeeId === employeeId && !r.isDeleted && r.hours > 0
        );

        const escapeHtml = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        const jobRowHtml = (jobId, hours) => {
            const job = jobs.find(j => j.id === jobId);
            const code = escapeHtml(timesheetService.getJobCode(job) || String(jobId));
            const name = escapeHtml(timesheetService.getJobName(job) || '');
            return `<div style="display:flex;justify-content:space-between;align-items:center;padding:3px 0;gap:8px;">
                <div style="min-width:0;overflow:hidden;">
                    <div style="color:#1976d2;font-weight:700;font-size:13px;">#${code}</div>
                    ${name ? `<div style="font-size:11px;color:#888;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${name}</div>` : ''}
                </div>
                <span style="font-weight:600;color:#c62828;flex-shrink:0;">${hours}h</span>
            </div>`;
        };

        let detailsHtml = '';
        if (employeeWeekRecords.length > 0) {
            const totalHours = employeeWeekRecords.reduce((sum, r) => sum + r.hours, 0);
            const byDay = {};
            employeeWeekRecords.forEach(r => {
                const day = getWeekdayName(r.workDate);
                const dateStr = formatWeekEndingDate(r.workDate);
                const key = `${day} ${dateStr}`;
                if (!byDay[key]) byDay[key] = { jobs: [], hours: 0 };
                byDay[key].jobs.push({ jobId: r.jobId, hours: r.hours });
                byDay[key].hours += r.hours;
            });

            detailsHtml = `<div style="text-align:left;margin-top:12px;font-size:13px;">
                <div style="font-weight:600;margin-bottom:6px;">Will be deleted for the week (${totalHours}h total):</div>
                <div>${Object.entries(byDay).map(([day, data]) =>
                    `<details style="margin:4px 0;border:1px solid #eee;border-radius:6px;background:#fafafa;">
                        <summary style="padding:6px 10px;cursor:pointer;display:flex;justify-content:space-between;font-size:13px;">
                            <strong>${escapeHtml(day)}</strong>
                            <span style="color:#c62828;font-weight:700;">${data.hours}h</span>
                        </summary>
                        <div style="padding:4px 12px 8px 12px;">${data.jobs.map(j => jobRowHtml(j.jobId, j.hours)).join('')}</div>
                    </details>`
                ).join('')}</div>
            </div>`;
        }

        const result = await Swal.fire({
            title: 'Remove employee?',
            html: `Remove <strong>${escapeHtml(employeeName)}</strong> from ${getWeekdayName(currentDate)}?${detailsHtml}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#757575',
            confirmButtonText: employeeWeekRecords.length > 0 ? 'Delete All & Remove' : 'Remove',
            width: 480,
        });

        if (result.isConfirmed) {
            if (activeCell?.employeeId === employeeId) dismissKeyboard();
            await removeEmployeeFromDay(employeeId);
        }
    }, [weekRecords, jobs, currentDate, removeEmployeeFromDay, activeCell, dismissKeyboard]);

    const handleRemoveJob = useCallback(async (jobId, jobName) => {
        const jobWeekRecords = weekRecords.filter(
            r => r.jobId === jobId && !r.isDeleted && r.hours > 0
        );

        const escapeHtml = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        const job = jobs.find(j => j.id === jobId);
        const jobCode = escapeHtml(timesheetService.getJobCode(job) || String(jobId));
        const jobDisplayName = escapeHtml(timesheetService.getJobName(job) || jobName || '');
        // В шапке колонки дивизион и клиент живут сокращёнными («35·DHG») —
        // здесь единственное место, где форман видит их целиком.
        const jobMeta = [
            divisionOf(job) ? `Division ${escapeHtml(divisionOf(job))}` : '',
            timesheetService.getJobCustomer(job) ? escapeHtml(timesheetService.getJobCustomer(job)) : '',
        ].filter(Boolean).join(' · ');

        const jobHeader = `<div style="text-align:left;margin:4px 0 8px 0;">
            <div style="color:#1976d2;font-weight:700;font-size:16px;">#${jobCode}</div>
            ${jobDisplayName ? `<div style="font-size:12px;color:#666;">${jobDisplayName}</div>` : ''}
            ${jobMeta ? `<div style="font-size:12px;color:#666;">${jobMeta}</div>` : ''}
        </div>`;

        let detailsHtml = '';
        if (jobWeekRecords.length > 0) {
            const totalHours = jobWeekRecords.reduce((sum, r) => sum + r.hours, 0);
            const byEmployee = {};
            jobWeekRecords.forEach(r => {
                const emp = employees.find(e => e.id === r.employeeId);
                const empName = timesheetService.getEmployeeName(emp) || 'Employee ' + r.employeeId;
                if (!byEmployee[empName]) byEmployee[empName] = { days: [], hours: 0 };
                byEmployee[empName].days.push(`${getWeekdayName(r.workDate)} (${r.hours}h)`);
                byEmployee[empName].hours += r.hours;
            });

            detailsHtml = `<div style="text-align:left;margin-top:8px;font-size:13px;">
                <div style="font-weight:600;margin-bottom:6px;">Will be deleted for the week (${totalHours}h total):</div>
                <div>${Object.entries(byEmployee).map(([name, data]) =>
                    `<div style="margin-bottom:6px;padding:4px 8px;background:#fafafa;border-radius:4px;">
                        <div style="display:flex;justify-content:space-between;">
                            <strong>${escapeHtml(name)}</strong>
                            <span style="color:#c62828;font-weight:700;">${data.hours}h</span>
                        </div>
                        <div style="color:#6b7280;font-size:11px;">${escapeHtml(data.days.join(', '))}</div>
                    </div>`
                ).join('')}</div>
            </div>`;
        }

        const result = await Swal.fire({
            title: 'Remove job?',
            html: `${jobHeader}${detailsHtml}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#757575',
            confirmButtonText: jobWeekRecords.length > 0 ? 'Delete All & Remove' : 'Remove',
            width: 480,
        });

        if (result.isConfirmed) {
            if (activeCell?.jobId === jobId) dismissKeyboard();
            await removeJobFromList(jobId);
        }
    }, [weekRecords, employees, jobs, removeJobFromList, activeCell, dismissKeyboard]);

    // ─── Clear Week ─────────────────────────────────────────────────────────

    const handleOpenClearWeek = useCallback(() => { dismissKeyboard(); setClearScope('week'); }, [dismissKeyboard]);
    const handleOpenClearDay = useCallback(() => { dismissKeyboard(); setClearScope('day'); }, [dismissKeyboard]);


    // ─── Calendar ───────────────────────────────────────────────────────────

    const loadCalendarMonth = useCallback(async (year, month) => {
        setCalendarLoading(true);
        try {
            const data = await fetchCalendarData(year, month);
            setCalendarData(data);
        } finally {
            setCalendarLoading(false);
        }
    }, [fetchCalendarData]);

    const openCalendar = useCallback(async () => {
        setCalendarModal(true);
        const d = new Date(currentDate + 'T00:00:00');
        await loadCalendarMonth(d.getFullYear(), d.getMonth());
    }, [currentDate, loadCalendarMonth]);

    const handleCalendarSelect = useCallback(async (date) => {
        if (keyboardOpen && activeCell && activeCell.workDate === currentDate) {
            await saveKeyboardCell();
        }
        carryCellTo(date);
        loadDate(date);
        setCalendarModal(false);
    }, [loadDate, keyboardOpen, activeCell, currentDate, saveKeyboardCell, carryCellTo]);

    // ─── Sign/Submit ────────────────────────────────────────────────────────

    const handleSignCustomer = useCallback(async (jobIds, customer, payload = {}) => {
        const weekEnd = timesheetService.getWeekEndingDate(currentDate);
        // payload собран в SignTimesheetModal: подписи форемана (по умолчанию) и
        // заказчика (опц.), items (+per-item note), foremanComment, dayNarratives.
        // Всё сохраняется в SignedTimesheet record и при sync уйдёт одним POST'ом.
        const result = await signAllTimesheets(jobIds, weekEnd, {
            foremanSignatureImage: payload.foremanSignatureImage || '',
            foremanName: payload.foremanName || '',
            customerSignatureImage: payload.customerSignatureImage || '',
            signerName: payload.signerName || '',
            items: payload.items || [],
            foremanComment: payload.foremanComment || '',
            dayNarratives: payload.dayNarratives || null,
            emailPdf: !!payload.emailPdf,
        });
        if (result.successCount > 0) {
            Swal.fire({
                icon: 'success',
                title: 'Signed',
                text: `${customer}: ${result.successCount} job${result.successCount > 1 ? 's' : ''}`,
                timer: 1500,
                showConfirmButton: false,
            });
        }
    }, [currentDate, signAllTimesheets]);

    // ─── Copy last week (lists only) ────────────────────────────────────────

    // Только в понедельник: неделю собирают в её начале. Часы не переносятся —
    // только люди и работы.
    // Только понедельник и только пока на неделе нет часов: неделя уже начата —
    // копировать прошлую незачем (юзер 10/15).
    const canCopyLastWeek = !isReadOnly && new Date(currentDate + 'T00:00:00').getDay() === 1
        && !weekRecords.some(r => !r.isDeleted && r.hours > 0);

    const handleCopyLastWeek = useCallback(async () => {
        const prev = await getPreviousWeekSelections();
        const curEmp = new Set(selectedEmployeeIds);
        const curJobs = new Set(selectedJobIds);
        const newEmp = prev.employeeIds.filter(id => !curEmp.has(id) && employees.some(e => e.id === id));
        const newJobs = prev.jobIds.filter(id => !curJobs.has(id) && jobs.some(j => j.id === id));
        const range = `${formatWeekEndingDate(prev.weekStart)} – ${formatWeekEndingDate(prev.weekEnd)}`;

        if (newEmp.length === 0 && newJobs.length === 0) {
            Swal.fire({
                icon: 'info',
                title: 'Nothing to copy',
                text: prev.employeeIds.length || prev.jobIds.length
                    ? `Everyone and every job from last week (${range}) is already here.`
                    : `Last week (${range}) has no employees or jobs on this iPad.`,
            });
            return;
        }
        const result = await Swal.fire({
            title: 'Copy last week?',
            html: `Add <b>${newEmp.length}</b> employee${newEmp.length === 1 ? '' : 's'} and <b>${newJobs.length}</b> job${newJobs.length === 1 ? '' : 's'} from last week (${range}) to this week.<br><br><small>Hours are not copied — only the lists.</small>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Copy',
            confirmButtonColor: '#9c27b0',
        });
        if (!result.isConfirmed) return;
        await addToSelections(newEmp, newJobs);
        Swal.fire({ icon: 'success', title: 'Copied', text: `${newEmp.length} employees and ${newJobs.length} jobs added`, timer: 1500, showConfirmButton: false });
    }, [getPreviousWeekSelections, addToSelections, selectedEmployeeIds, selectedJobIds, employees, jobs]);

    // ─── Copy previous day's hours ──────────────────────────────────────────

    // Правило «строго вчера, внутри недели» — в copyDay.js.
    const copyDayPlan = useMemo(() => (isReadOnly ? null : previousDayPlan({
        currentDate, weekRecords, selectedEmployeeIds, selectedJobIds, currentUserId, recordByCell,
    })), [isReadOnly, weekRecords, currentDate, selectedEmployeeIds, selectedJobIds, currentUserId, recordByCell]);

    const handleCopyDay = useCallback(async () => {
        if (!copyDayPlan) return;
        if (!(await confirmFutureDate(currentDate))) return;
        const { from } = copyDayPlan;
        const entries = copyDayPlan.entries;
        const total = entries.reduce((s, e) => s + e.hours, 0);
        const result = await Swal.fire({
            title: `Copy ${formatDayLabel(from)}?`,
            html: `Add <b>${entries.length}</b> entr${entries.length === 1 ? 'y' : 'ies'} (<b>${Math.round(total * 100) / 100} h</b>) from <b>${formatDayLabel(from)}</b> to <b>${formatDayLabel(currentDate)}</b>.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Copy',
            confirmButtonColor: '#1976d2',
        });
        if (!result.isConfirmed) return;
        // Клетка под открытой клавиатурой: была ли она пустой до копии.
        const kbCell = keyboardOpen && activeCell && activeCell.workDate === currentDate ? activeCell : null;
        const kbCellWasEmpty = kbCell && !findCellRecord(kbCell.employeeId, kbCell.jobId);
        try {
            const res = await copyEntriesToCurrentDay(entries);
            // Клавиатура держит свой буфер и синхронизируется только при смене
            // клетки/дня. Без этого активная клетка после копии показывала «0»,
            // а закрытие клавиатуры или стрелка сохраняли этот 0 поверх
            // скопированных часов (Scott, 10/18). Клетку, где уже что-то было,
            // копия не трогает — её буфер не меняем.
            if (kbCellWasEmpty) {
                const hit = entries.find(e => e.employeeId === kbCell.employeeId && e.jobId === kbCell.jobId);
                if (hit && !res.overLimit.includes(hit)) {
                    setKeyboardValue(String(hit.hours));
                    kbSyncRef.current.typed = false;
                }
            }
            const skipped = res.overLimit.length
                ? `<br><small>${res.overLimit.length} entr${res.overLimit.length === 1 ? 'y was' : 'ies were'} skipped: the person would go over 24 h today.</small>`
                : '';
            Swal.fire({
                icon: res.overLimit.length ? 'warning' : 'success',
                title: 'Copied',
                html: `${res.added} entr${res.added === 1 ? 'y' : 'ies'}, ${Math.round(res.hours * 100) / 100} h${skipped}`,
                timer: res.overLimit.length ? undefined : 1500,
                showConfirmButton: res.overLimit.length > 0,
            });
        } catch (e) {
            console.error('[Timesheet] copy day failed:', e);
            Swal.fire({ icon: 'error', title: 'Copy stopped', text: `Some entries were not copied: ${e?.message || 'unknown error'}` });
        }
    }, [copyDayPlan, currentDate, confirmFutureDate, copyEntriesToCurrentDay, keyboardOpen, activeCell, findCellRecord]);

    // ─── Render ─────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, height: 'calc(100vh - 60px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <TimesheetSyncStatusBar />
            {/* Page header + inline legend in one row */}
            {(() => {
                const items = [
                    // ─── Sync lifecycle (что происходит при сохранении на сервер) ───
                    {
                        key: 'pending', group: 'sync',
                        icon: <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: '#fef3c7', border: '2px solid #d97706' }} />,
                        label: 'Pending',
                        desc: 'Hours saved locally, not yet synced to server',
                    },
                    {
                        key: 'synced', group: 'sync',
                        icon: <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: '#dcfce7', border: '2px solid #16a34a' }} />,
                        label: 'Synced',
                        desc: 'Hours are saved and confirmed on the server',
                    },
                    {
                        key: 'error', group: 'sync',
                        icon: <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: '#fee2e2', border: '2px solid #dc2626' }} />,
                        label: 'Error',
                        desc: 'Sync failed — will retry automatically',
                    },
                    // ─── Workflow (что делает foreman / admin с записью) ──────────────
                    {
                        key: 'signed', group: 'flow',
                        icon: (
                            <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: '#dcfce7', borderLeft: '3px solid #16a34a', border: '1px solid #16a34a', position: 'relative', overflow: 'hidden' }}>
                                <Box sx={{ position: 'absolute', bottom: 0, left: 1, width: 6, height: 6, borderRadius: '50%', bgcolor: '#16a34a', color: '#fff', fontSize: '5px', lineHeight: '6px', textAlign: 'center', fontWeight: 700 }}>✓</Box>
                            </Box>
                        ),
                        label: 'Signed',
                        desc: 'Hours signed off by foreman — payroll-ready. If you edit them, the signature drops and the week must be signed again.',
                    },
                    {
                        key: 'rejected', group: 'flow',
                        icon: (
                            <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: '#fecaca', borderLeft: '3px solid #dc2626', border: '1px solid #dc2626', position: 'relative', overflow: 'hidden' }}>
                                <Box sx={{ position: 'absolute', bottom: 0, left: 1, width: 6, height: 6, borderRadius: '50%', bgcolor: '#dc2626', color: '#fff', fontSize: '6px', lineHeight: '6px', textAlign: 'center', fontWeight: 700 }}>!</Box>
                            </Box>
                        ),
                        label: 'Rejected',
                        desc: 'Admin rejected this entry with a reason. Tap the cell — a modal will open with the reason and a field for corrected hours.',
                    },
                    {
                        key: 'approved', group: 'flow',
                        icon: (
                            <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: '#ede9fe', borderLeft: '3px solid #7c3aed', border: '1px solid #c4b5fd', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start' }}>
                                <Box sx={{ fontSize: '9px', lineHeight: '10px', pl: '1px' }}>🔒</Box>
                            </Box>
                        ),
                        label: 'Approved',
                        desc: 'Admin approved hours for payroll — sent to ADP. Whole week is locked for editing. Ask admin to unapprove first if a correction is really needed.',
                    },
                    {
                        key: 'active', group: 'flow',
                        icon: <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: '#eff6ff', border: '3px solid #1976d2' }} />,
                        label: 'Active',
                        desc: 'Currently editing — thick blue frame; the cell keeps its status color and badges. Use the keyboard below to enter hours.',
                    },
                    {
                        key: 'attend', group: 'flow',
                        icon: (
                            <Box sx={{ px: 0.5, borderRadius: '4px', bgcolor: '#dc2626', color: '#fff', fontSize: '7px', fontWeight: 700, lineHeight: '13px' }}>NC</Box>
                        ),
                        label: 'Absence code',
                        desc: 'Red badge in a cell: NC = No Call No Show, EX = Excused, UN = Unexcused, TM = Termed - ADP entered. Long-press a cell → "Absence code" to set it. An absence day can stay at 0 hours.',
                    },
                    {
                        key: 'night', group: 'flow',
                        icon: (
                            <Box sx={{ px: 0.5, borderRadius: '4px', bgcolor: '#312e81', color: '#fff', fontSize: '7px', fontWeight: 700, lineHeight: '13px' }}>NW</Box>
                        ),
                        label: 'Night work',
                        desc: 'Indigo NW badge in a cell = this WORKER is on night shift that day (payroll gets "Night work"; one worker can be on days, another on nights on the same job). HOW TO SET: long-press the worker’s cell → Attendance code → "Night work". It auto-applies to that worker’s remaining days of the week; any day can be changed back.',
                    },
                    // ─── Итоги в шапках колонок ───────────────────────────────────────
                    {
                        key: 'daytotal', group: 'totals',
                        icon: <Box sx={{ px: '3px', borderRadius: '3px', bgcolor: '#e65100', color: '#fff', fontSize: '7px', fontWeight: 700, lineHeight: '13px' }}>12</Box>,
                        label: 'Day total',
                        desc: 'Orange number in the top-left corner of a job header — hours on that job today for everyone in the list. On the Day column — everyone\'s hours today.',
                    },
                    {
                        key: 'weektotal', group: 'totals',
                        icon: <Box sx={{ px: '3px', borderRadius: '3px', bgcolor: '#2e7d32', color: '#fff', fontSize: '7px', fontWeight: 700, lineHeight: '13px' }}>64</Box>,
                        label: 'Week total',
                        desc: 'Green number in the bottom-right corner of a job header — hours on that job for the whole week (Mon–Sun) for everyone in the list. On the Week column — everyone\'s hours this week. Big numbers are shortened to fit: 128.25 shows as 128, 1061 as 1.1k (about 1,100 h).',
                    },
                    // ─── Notes (типы заметок) ─────────────────────────────────────────
                    {
                        key: 'cellnote', group: 'notes',
                        icon: <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: '#fff', border: '1px solid #d0d0d0', position: 'relative', overflow: 'hidden',
                            '&::after': { content: '""', position: 'absolute', top: 0, right: 0, borderStyle: 'solid', borderWidth: '0 8px 8px 0', borderColor: 'transparent #d97706 transparent transparent' },
                        }} />,
                        label: 'Cell note',
                        desc: 'Note attached to a specific hour entry — long-press the cell to view or edit',
                    },
                    {
                        key: 'empnote', group: 'notes',
                        icon: (
                            <Box sx={{ display: 'inline-flex', gap: 0.25, alignItems: 'center' }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ea580c' }} />
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2563eb' }} />
                            </Box>
                        ),
                        label: 'Emp. note',
                        desc: 'Employee has a note today. Orange = per-job note, blue = general day note. Tap the Notes column to view or edit.',
                    },
                    {
                        key: 'jobnote', group: 'notes',
                        icon: <Box sx={{ width: 14, height: 14, borderRadius: '3px', border: '2px solid #7c3aed', bgcolor: '#ede9fe' }} />,
                        label: 'Job note',
                        desc: 'Daily note for this job — job column header is highlighted purple',
                    },
                ];
                const groupLabels = { sync: 'Sync', flow: 'Status', totals: 'Totals', notes: 'Notes' };
                const groupOrder = ['sync', 'flow', 'totals', 'notes'];
                return (
                    <Box sx={{ mb: 1 }}>
                        {/* Title row + legend toggle — всё в одну линию */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton onClick={() => navigate('/main')} edge="start" size="small">
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="subtitle1" fontWeight={700}>Timesheet</Typography>
                            {/* Вся неделя на одном экране — посмотреть, не правя. */}
                            <Box
                                component="button"
                                onClick={() => setWeekViewOpen(true)}
                                sx={{
                                    ml: 0.75, display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                    border: '1px solid', borderColor: 'primary.main', color: 'primary.main',
                                    bgcolor: '#fff', borderRadius: '18px', px: 1.25, py: 0.4, minHeight: 32,
                                    fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
                                    '&:active': { bgcolor: '#eef2ff' },
                                }}
                                title="All hours of the week on one screen"
                            >
                                Week view
                            </Box>

                            {/* Compact legend pill: иконки всех статусов в одну строку. Клик — expand. */}
                            <Box
                                component="button"
                                onClick={() => setLegendOpen(legendOpen === '__all__' ? null : '__all__')}
                                sx={{
                                    ml: 'auto',
                                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                    border: '1px solid',
                                    borderColor: legendOpen === '__all__' ? 'primary.main' : '#e2e8f0',
                                    bgcolor: legendOpen === '__all__' ? '#eef2ff' : '#fff',
                                    borderRadius: '18px', px: 1, py: 0.35,
                                    cursor: 'pointer',
                                    '&:hover': { borderColor: 'primary.main', bgcolor: '#f1f5f9' },
                                }}
                                title="Show what each color / icon means"
                            >
                                <Typography sx={{ fontSize: '0.6rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em', mr: 0.25 }}>Legend</Typography>
                                {items.map(item => (
                                    <Box key={item.key} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                        {item.icon}
                                    </Box>
                                ))}
                                <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', ml: 0.25 }}>
                                    {legendOpen === '__all__' ? '▴' : '▾'}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Expanded legend — full explanation, shown только при клике */}
                        {legendOpen === '__all__' && (
                            <Box sx={{
                                mt: 0.75, px: 1.5, py: 1,
                                bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5,
                                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5,
                                fontSize: '0.7rem', color: '#475569', lineHeight: 1.4,
                            }}>
                                {items.map(item => (
                                    <Box key={item.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                        <Box sx={{ mt: 0.25, flexShrink: 0 }}>{item.icon}</Box>
                                        <Box>
                                            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', lineHeight: 1.2 }}>
                                                {item.label}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.35, color: '#64748b' }}>
                                                {item.desc}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                );
            })()}

            {/* Timesheet Header (week info, navigation, action buttons) */}
            <Box sx={{ mb: 1, flexShrink: 0 }}>
                <TimesheetHeader
                    currentDate={currentDate}
                    weekInfo={weekInfo}
                    weekRecords={weekRecords}
                    selectedJobs={selectedJobs}
                    isReadOnly={isReadOnly}
                    isWeekFullySigned={isWeekFullySigned}
                    maxNavigableDate={maxNavigableDate}
                    onNavigateDate={navigateDate}
                    onGoToToday={goToToday}
                    onOpenCalendar={openCalendar}
                    onClearWeek={handleOpenClearWeek}
                    onClearDay={handleOpenClearDay}
                    onOpenDailyNotes={() => setDailyNotesModal(true)}
                    onSign={() => setSignModal(true)}
                    canCopyLastWeek={canCopyLastWeek}
                    onCopyLastWeek={handleCopyLastWeek}
                    copyDayFrom={copyDayPlan?.from || null}
                    copyDayCount={copyDayPlan?.entries.length || 0}
                    onCopyDay={handleCopyDay}
                    approvedCount={approvedCount}
                    totalWeekRecords={totalWeekRecords}
                    isWeekFullyApproved={isWeekFullyApproved}
                />
            </Box>

            {/* Timesheet Table — fills remaining space */}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pb: `${keyboardHeight}px` }}>
            <TimesheetTable
                employees={employees}
                jobs={jobs}
                selectedEmployees={selectedEmployees}
                selectedJobIds={selectedJobIds}
                records={records}
                weekRecords={weekRecords}
                dailyNotes={dailyNotes}
                currentDate={currentDate}
                currentDateStr={currentDate}
                activeCell={activeCell}
                keyboardValue={keyboardValue}
                keyboardOpen={keyboardOpen}
                employeeSort={employeeSort}
                jobSortId={jobSortId}
                isReadOnly={isReadOnly}
                currentUserId={currentUserId}
                otherRecords={othersWeek}
                onSetEmployeeSort={handleSetEmployeeSort}
                onToggleJobSort={handleToggleJobSort}
                onCellShortPress={handleCellShortPress}
                onCellLongPress={handleCellLongPress}
                onOpenEmployeesModal={() => setEmployeesModal(true)}
                onOpenRosterModal={() => setRosterModal(true)}
                onOpenJobsModal={() => setJobsModal(true)}
                onRemoveEmployee={handleRemoveEmployee}
                onRemoveJob={handleRemoveJob}
                onOpenEmployeeNotes={handleOpenEmployeeNotes}
                onOpenOtherHours={handleOpenOtherHours}
            />
            </Box>

            {/* Keyboard */}
            <TimesheetKeyboard
                isOpen={keyboardOpen}
                value={keyboardValue}
                employeeName={activeCell ? timesheetService.getEmployeeName(employees.find(e => e.id === activeCell.employeeId)) : ''}
                jobName={activeCell ? timesheetService.getJobName(jobs.find(j => j.id === activeCell.jobId)) : ''}
                dayTotal={activeCell ? getDayTotal(activeCell.employeeId) : 0}
                onValueChange={handleKeyboardValueChange}
                onSubmit={async () => { const ok = await saveKeyboardCell(); if (ok) closeKeyboard(); }}
                onCancel={closeKeyboard}
                onNavigate={navigateCell}
                onHeightChange={setKeyboardHeight}
            />

            {/* ── Modals ───────────────────────────────────────────────────── */}

            {/* Employee & Temps selection — reuse JHA components */}
            <SelectEmployeesAndTempsModal
                open={employeesModal}
                onClose={() => setEmployeesModal(false)}
                onSelect={async (selectedPeople) => {
                    const ids = selectedPeople.map(p => {
                        // Employees: id field
                        // Temps: id or tempId — use negative to distinguish
                        if (p._type === 'temp') {
                            const tempId = p.id || p.tempId;
                            return tempId ? -Math.abs(Number(tempId)) : null;
                        }
                        return p.id || p.EE || p.ee || p.employeeId || null;
                    }).filter(id => id !== null).map(Number);
                    // Also merge temp objects into employees list so table can render names
                    const newTemps = selectedPeople.filter(p => p._type === 'temp');
                    if (newTemps.length > 0) {
                        setEmployees(prev => {
                            const existing = new Set(prev.map(e => e.id));
                            const toAdd = newTemps
                                .map(t => ({
                                    ...t,
                                    id: -Math.abs(Number(t.id || t.tempId)),
                                    fullName: t.FullName || t.fullName || 'Unknown',
                                    FullName: t.FullName || t.fullName || 'Unknown',
                                    _type: 'temp',
                                }))
                                .filter(t => !existing.has(t.id));
                            return [...prev, ...toAdd];
                        });
                    }
                    setEmployeesModal(false);
                    // Сняли людей с часами — сначала предупреждение (кто и за какие дни потеряет время).
                    const kept = await confirmDroppedEmployees(selectedEmployeeIds.filter(id => !ids.includes(id)));
                    // Оставленные — на своих местах, новые — в конце, как раньше.
                    const keep = new Set([...ids, ...kept]);
                    setSelectedEmployees([...selectedEmployeeIds.filter(id => keep.has(id)), ...ids.filter(id => !selectedEmployeeIds.includes(id))]);
                }}
                alreadySelected={
                    selectedEmployeeIds.map(id => {
                        const emp = employees.find(e => e.id === id);
                        if (!emp) return null;
                        const isTemp = emp._type === 'temp';
                        return {
                            ...emp,
                            // Restore original positive ID for matching in JHA modal
                            id: isTemp ? Math.abs(id) : id,
                            tempId: isTemp ? Math.abs(id) : undefined,
                            FullName: timesheetService.getEmployeeName(emp),
                            _type: isTemp ? 'temp' : 'employee',
                        };
                    }).filter(Boolean)
                }
                employeesOnly={false}
                enableDivisionFilter
                divisionFilterStorageKey="timesheetEmployeesDivisionFilter"
                showSelectedStrip
                pinSelfFirst
            />

            {/* Import from Weekly Roster */}
            <SelectFromWeeklyRosterModal
                open={rosterModal}
                onClose={() => setRosterModal(false)}
                onSelect={(selectedPeople) => {
                    const newIds = selectedPeople.map(p => {
                        if (p._type === 'temp') {
                            const tempId = p.id || p.tempId;
                            return tempId ? -Math.abs(Number(tempId)) : null;
                        }
                        return p.id || p.EE || p.ee || p.employeeId || null;
                    }).filter(id => id !== null).map(Number);
                    // Also merge temp objects
                    const newTemps = selectedPeople.filter(p => p._type === 'temp');
                    if (newTemps.length > 0) {
                        setEmployees(prev => {
                            const existing = new Set(prev.map(e => e.id));
                            const toAdd = newTemps
                                .map(t => ({
                                    ...t,
                                    id: -Math.abs(Number(t.id || t.tempId)),
                                    fullName: t.FullName || t.fullName || 'Unknown',
                                    FullName: t.FullName || t.fullName || 'Unknown',
                                    _type: 'temp',
                                }))
                                .filter(t => !existing.has(t.id));
                            return [...prev, ...toAdd];
                        });
                    }
                    // Merge with existing (don't replace)
                    const merged = [...new Set([...selectedEmployeeIds, ...newIds])];
                    setSelectedEmployees(merged);
                    setRosterModal(false);
                }}
                alreadySelected={
                    selectedEmployeeIds.map(id => {
                        const emp = employees.find(e => e.id === id);
                        if (!emp) return null;
                        const isTemp = emp._type === 'temp';
                        return {
                            ...emp,
                            id: isTemp ? Math.abs(id) : id,
                            tempId: isTemp ? Math.abs(id) : undefined,
                            FullName: timesheetService.getEmployeeName(emp),
                            _type: isTemp ? 'temp' : 'employee',
                        };
                    }).filter(Boolean)
                }
            />

            <SelectJobsModal
                open={jobsModal}
                onClose={() => setJobsModal(false)}
                jobs={jobs}
                selectedIds={selectedJobIds}
                onSave={setSelectedJobs}
                enableDivisionFilter
                divisionFilterStorageKey="timesheetJobsDivisionFilter"
                hideJobsWithCostDoc
            />

            <CellEditModal
                open={cellEditModal.open}
                onClose={() => setCellEditModal({ open: false })}
                employeeName={cellEditModal.employeeName}
                jobName={cellEditModal.jobName}
                hours={cellEditModal.hours}
                notes={cellEditModal.notes}
                attendCode={cellEditModal.attendCode}
                weekSummary={cellEditModal.weekSummary}
                weekDates={cellEditModal.weekDates}
                currentDate={currentDate}
                weekAttend={cellEditModal.weekAttend}
                onSave={handleSaveCellEdit}
            />

            <RejectionCorrectionModal
                open={rejectionModal.open}
                employeeName={rejectionModal.employeeName}
                jobName={rejectionModal.jobName}
                jobCode={rejectionModal.jobCode}
                workDate={rejectionModal.workDate}
                originalHours={rejectionModal.originalHours}
                rejectionReason={rejectionModal.rejectionReason}
                rejectedByName={rejectionModal.rejectedByName}
                rejectedAt={rejectionModal.rejectedAt}
                onCancel={() => setRejectionModal({ open: false })}
                onConfirm={async (newHours) => {
                    // Сохраняем новые часы через updateRecord — серверный syncRecord
                    // увидит что запись была rejected, выставит rejection_cleared_at
                    // и переведёт status в 'draft'. Подпись недели тоже рассыпется
                    // (invalidateSignedWeek), foreman подпишет заново.
                    try {
                        const existing = findCellRecord(rejectionModal.employeeId, rejectionModal.jobId);
                        if (!existing) throw new Error('Record not found');
                        await updateRecord(
                            existing.clientId,
                            newHours,
                            existing.attendCode || '',
                            existing.jobCode || '',
                            existing.notes || '',
                            { clearRejection: true },
                        );
                        setRejectionModal({ open: false });
                    } catch (e) {
                        console.error('[Timesheet] rejection correction failed:', e);
                        alert('Could not save correction: ' + (e?.message || 'unknown error'));
                    }
                }}
                onKeep={async () => {
                    // Часы верные: пересохраняем как есть — сервер снимет спор (status → draft).
                    try {
                        const existing = findCellRecord(rejectionModal.employeeId, rejectionModal.jobId);
                        if (!existing) throw new Error('Record not found');
                        await updateRecord(
                            existing.clientId,
                            existing.hours,
                            existing.attendCode || '',
                            existing.jobCode || '',
                            existing.notes || '',
                            { clearRejection: true, skipLimitCheck: true },
                        );
                        setRejectionModal({ open: false });
                    } catch (e) {
                        console.error('[Timesheet] keep disputed hours failed:', e);
                        alert('Could not save: ' + (e?.message || 'unknown error'));
                    }
                }}
            />

            <DailyNotesModal
                open={dailyNotesModal}
                onClose={() => setDailyNotesModal(false)}
                jobs={selectedJobs}
                records={records}
                dailyNotes={dailyNotes}
                currentDate={currentDate}
                onSave={addDailyNote}
            />

            <EmployeeNotesModal
                open={employeeNotesModal.open}
                onClose={() => setEmployeeNotesModal({ open: false, employeeId: 0, employeeName: '' })}
                employeeName={employeeNotesModal.employeeName}
                employeeId={employeeNotesModal.employeeId}
                jobs={selectedJobs}
                allJobs={jobs}
                onAddJobs={(ids) => addToSelections([], ids)}
                records={records}
                weekRecords={weekRecords}
                dailyNotes={dailyNotes}
                currentDate={currentDate}
                onSaveNote={handleSaveEmployeeJobNotes}
                onSaveGeneralNote={(empId, notes) => addDailyNote(0, notes, empId)}
                onSetAttendCode={handleSetEmployeeAttendCode}
            />

            <EmployeeOtherHoursModal
                open={otherHoursModal.open}
                onClose={() => setOtherHoursModal({ open: false, employeeName: '', records: [] })}
                employeeName={otherHoursModal.employeeName}
                weekStart={weekInfo?.weekStart}
                weekEnd={weekInfo?.weekEnd}
                records={otherHoursModal.records}
                jobs={jobs}
            />

            <CalendarPickerModal
                open={calendarModal}
                onClose={() => setCalendarModal(false)}
                currentDate={currentDate}
                maxNavigableDate={maxNavigableDate}
                calendarData={calendarData}
                calendarLoading={calendarLoading}
                onSelectDate={handleCalendarSelect}
                onMonthChange={loadCalendarMonth}
            />

            <WeekViewModal
                open={weekViewOpen}
                onClose={() => setWeekViewOpen(false)}
                jobs={jobs}
                employees={employees}
                weekRecords={weekRecords}
                currentDate={currentDate}
            />

            <SignTimesheetModal
                open={signModal}
                onClose={() => setSignModal(false)}
                jobs={selectedJobs}
                employees={employees}
                weekRecords={weekRecords}
                weekDailyNotes={weekDailyNotes}
                weekInfo={weekInfo}
                currentDate={currentDate}
                onSign={handleSignCustomer}
            />

            <ClearTimesheetModal
                open={clearScope !== null}
                onClose={() => setClearScope(null)}
                scope={clearScope || 'week'}
                currentDate={currentDate}
                weekInfo={weekInfo}
                weekRecords={weekRecords}
                jobs={jobs}
                employees={employees}
                onDeleteRecords={deleteRecordsByClientIds}
                onClearAll={async () => {
                    if (clearScope === 'day') {
                        await clearDay();
                    } else {
                        await clearWeek();
                    }
                }}
            />
        </Box>
    );
}
